/**
 * useChordGrips
 *
 * Lädt die Griffe eines Akkords (Tab „Akkorde") via `POST /api/chord-grips`. `null` als Anfrage =
 * kein Request. Die Antwort ist je Anfrage deterministisch, daher vue-query mit
 * `staleTime: Infinity` – ein Zurückschalten auf einen schon gesehenen Akkord kostet keinen
 * Request. Der Key ist das JSON der Anfrage; es ist klein (Grundton, Typ, Offset, Bass-Layout).
 * Bis die neue Antwort da ist, bleibt die vorige stehen (`keepPreviousData`) – sonst unmounteten
 * die Karten bei jeder Bass-Belegungs-Änderung und verlören die gewählte Variante.
 */
import {computed, type MaybeRefOrGetter, toValue} from 'vue';
import {keepPreviousData, useQuery, useQueryClient} from '@tanstack/vue-query';

import {apiClient} from '@/api-client';
import type {ChordGripsRequest, ChordGripsResponse} from '@/components/harmonika/gripTypes';

const CHORD_GRIPS_QUERY_PREFIX = 'chord-grips';

export function useChordGrips(request: MaybeRefOrGetter<ChordGripsRequest | null>) {
    const queryClient = useQueryClient();
    const query = useQuery(
        computed(() => {
            const body = toValue(request);
            return {
                queryKey: [CHORD_GRIPS_QUERY_PREFIX, body ? JSON.stringify(body) : 'idle'] as const,
                enabled: body !== null,
                queryFn: ({signal}: {signal: AbortSignal}) => apiClient.chordGrips(body!, {signal}),
                staleTime: Infinity,
                placeholderData: keepPreviousData,
                retry: false as const,
                refetchOnWindowFocus: false,
            };
        }),
        queryClient,
    );

    const response = computed<ChordGripsResponse | null>(() => query.data.value ?? null);
    const error = computed<string | null>(() => query.error.value?.message ?? null);
    return {response, loading: query.isFetching, error};
}
